import * as React from "react";

interface TitleContextType {
  setTitle: (title: string) => void;
  resetTitle: () => void;
}

const DEFAULT_TITLE = "PathFinder";

const TitleContext = React.createContext<TitleContextType>({
  setTitle: () => {},
  resetTitle: () => {}
});

export const useTitleContext = () => React.useContext(TitleContext);

interface TitleProviderProps {
  children: React.ReactNode;
}

export const TitleProvider: React.FC<TitleProviderProps> = ({ children }) => {
  const [title, setTitleState] = React.useState<string>(DEFAULT_TITLE);

  React.useEffect(() => {
    document.title = title;
  }, [title]);

  const contextValue = React.useMemo(() => ({
    setTitle: (newTitle: string) => setTitleState(newTitle),
    resetTitle: () => setTitleState(DEFAULT_TITLE)
  }), []);

  return (
    <TitleContext.Provider value={contextValue}>
      {children}
    </TitleContext.Provider>
  );
};

export const usePageTitle = (title?: string, withSuffix = false) => {
  const { setTitle, resetTitle } = useTitleContext();

  React.useEffect(() => {
    if (!title) return;

    const formattedTitle = withSuffix ? `${title} - ${DEFAULT_TITLE}` : title;
    setTitle(formattedTitle);

    return resetTitle;
  }, [title, withSuffix, setTitle, resetTitle]);
};
